<template>
  <div>
    <b-row>
      <b-col cols="6">
        <Breadcrumb
          :nameNav="nameNav"
          :itemFilter="itemFilter"
          :searchQuery="searchQuery"
          @itemFilterSearch="addItemFilterSearch"
        />
      </b-col>
      <b-col cols="6" class="pr-3 my-2 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>
    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters :status-filter.sync="statusFilter" :status-options="statusOptions" />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card-group deck :class="{ 'mx-2': showFilters, 'm-0': !showFilters }">
      <b-card class="m-0" no-body>
        <div class="m-2">
          <!-- Table Top -->
          <b-row>
            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label class="text-capitalize">{{ $t("table.show") }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
                data-cy="per-page-selector"
              />
              <label class="text-capitalize">{{ $t("table.entries") }}</label>
            </b-col>

            <!-- Search -->
            <b-col cols="12" md="6">
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  data-cy="search-input"
                  v-model="searchQuery"
                  class="d-inline-block"
                  :placeholder="$t('table.search') + '...'"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-table
          ref="refListTable"
          class="position-relative"
          :items="fetchList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          :empty-text="$t('no_record_found')"
          :sort-desc.sync="isSortDirDesc"
          @row-selected="onRowSelected"
          select-mode="single"
          selectable
        >
          <template #head()="data">
            <span> {{ $tc(data.label, 1) }}</span>
          </template>

          <template #cell(caller)="data">
            <span> {{ resolverCallRol(data.item.devices, "caller") }}</span>
          </template>

          <template #cell(receiver)="data">
            <span> {{ resolverCallRol(data.item.devices, "receiver") }}</span>
          </template>

          <template #head(actions)="data">
            <span> {{ $tc(data.label, 2) }}</span>
          </template>

          <template #cell(recording_url)="data">
            <audio
              controls
              v-if="data.item.recording_url"
              :controlsList="$can('download', 'PbxCall') ? 'download' : 'nodownload'"
            >
              <source :src="data.item.recording_url" />
              Your browser does not support the audio element.
            </audio>
          </template>

          <!-- Column: status -->
          <template #cell(name)="data">
            <div style="display: inline-flex">
              <div
                class="mr-1"
                :style="`background: ${data.item.cameras_proxy.online ? 'green' : 'red'};
                      width: 1.5rem;
                      height: 1.5rem;
                      border-radius: 1rem;
                      text-align: center;`"
              ></div>
              {{ data.item.name }}
            </div>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted text-capitalize">{{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <Pagination
                :totalList="totalList"
                :currentPage="currentPage"
                @updatePagination="update"
                :perPage="perPage"
                data-cy="pagination"
              >
              </Pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
      <!-- Modal -->
      <b-card
        class="mr-0 p-2"
        :class="!showFilters ? 'showScroll ml-0' : 'sticky-card ml-2'"
        v-if="selectedObject || loadingSelected"
        no-body
      >
        <feather-icon
          icon="XIcon"
          class="mr-2 ml-auto"
          style="cursor: pointer; position: absolute; right: 0px"
          size="30"
          @click="closeIfoSlot()"
        />
        <div>
          <!-- Info -->
          <pbx-call-slot :data="selectedObject" v-if="selectedObject" />
          <b-spinner
            v-else
            label="Spinning"
            variant="primary"
            class="spinner"
          ></b-spinner>
        </div>
      </b-card>
    </b-card-group>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import useList from "./useList";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Filters from "./Filters.vue";
import Pagination from "@/components/Pagination.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import PbxCallSlot from "../../components/GridView/layouts/slots/PbxCall";
import axios from "axios";
import { SOURCES } from "@/config/layoutConfig";

export default {
  components: {
    Breadcrumb,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    ToastificationLoadingContent,
    FilterModal,
    ButtonFilters,
    PbxCallSlot,
  },
  props: {
    filters: {
      type: Object | Array,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
  },
  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const action = ref("list");

    const toast = useToast();

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,

      refListTable,
      refetchData,
      statusOptions,
      statusFilter,

      selectedObject,
      loadingSelected,

      clearSearchFilters,
      filtersCount,
      buildFilters,
    } = useList();

    const onCopy = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: "Copiado correctamente",
          icon: "ClipboardIcon",
          variant: "success",
        },
      });
    };

    const onError = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: "No se pudo copiar",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    // start breadcrumbs
    const itemFilter = ref();
    const addItemFilter = (data) => {
      itemFilter.value = data;
    };

    const addItemFilterSearch = (data) => {
      data.forEach((element) => {
        if (nameNav.value === element.text) {
          searchQuery.value = element.searchQuery;
        }
      });
    };
    const nameNav = ref("breadcrumbs.voip.omnicloud.calls");
    // end breadcrumbs

    const resolverCallRol = (devices, rol) => {
      let caller = "";
      devices.forEach((element) => {
        if (element.call_device.rol === rol) {
          caller = element.extension;
        }
      });
      return caller;
    };

    const closeIfoSlot = () => {
      selectedObject.value = null;
      loadingSelected.value = false;
    };

    const onRowSelected = async (item) => {
      selectedObject.value = null;
      if (!item.length) {
        loadingSelected.value = false;
        return;
      }

      loadingSelected.value = true;

      let logs = {};

      try {
        const { data } = await axios.get(
          `https://pbx-manager.omniview.cloud${item[0].call_log}`
        );
        logs = data.data;
      } catch (error) {}
      selectedObject.value = {
        data: {
          ...item[0],
          logs,
          in_app_source: SOURCES.ALARM,
        },
      };
      loadingSelected.value = false;
    };

    return {
      action,
      itemFilter,
      addItemFilter,
      addItemFilterSearch,
      nameNav,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      update,
      onCopy,
      onError,

      filtersCount,
      buildFilters,
      clearSearchFilters,

      statusOptions,
      statusFilter,

      selectedObject,
      loadingSelected,

      resolverCallRol,

      onRowSelected,
      closeIfoSlot,
    };
  },
};
</script>

<style lang="scss" scoped>
.sticky-card {
  position: sticky;
  top: 6rem;
  height: 80vh;
  overflow: auto;
}

.spinner {
  margin-top: calc(50% - 100px);
  margin-left: 50%;
}

.showScroll {
  position: sticky;
  top: 6rem;
  height: auto;
  overflow: hidden;
}
</style>